import type { AdminLanguage, DashboardPeriod } from '@sirius/contracts/admin'
import type { TFunction } from 'i18next'
import { formatDateParts, intlLocale } from '~/i18n/format'
import { DASHBOARD_TIME_ZONE, isBerlinDate } from './dashboardFilters'

const hourly = (period: DashboardPeriod) => period === '24h'

const HOUR: Intl.DateTimeFormatOptions = {
  timeZone: DASHBOARD_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
}

const DAY: Intl.DateTimeFormatOptions = {
  timeZone: DASHBOARD_TIME_ZONE,
  month: 'short',
  day: 'numeric',
}

const WEEKDAY_HOUR: Intl.DateTimeFormatOptions = {
  timeZone: DASHBOARD_TIME_ZONE,
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
}

const WEEKDAY_YEAR: Intl.DateTimeFormatOptions = {
  timeZone: DASHBOARD_TIME_ZONE,
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
}

/**
 * A bucket's axis label in Berlin time: its hour (`14:00`) for the 24 hours,
 * its day (`Jan 8`) for 7 or 30 days, in the panel language.
 */
export function formatBucketLabel(
  bucketStart: Date,
  period: DashboardPeriod,
  language?: AdminLanguage,
) {
  return formatDateParts(bucketStart, hourly(period) ? HOUR : DAY, language)
}

/**
 * A bucket's tooltip title in Berlin time: `Thu, Jan 8, 14:00` for an hour,
 * `Thu, Jan 8, 2026` for a day, in the panel language.
 */
export function formatBucketTitle(
  bucketStart: Date,
  period: DashboardPeriod,
  language?: AdminLanguage,
) {
  return formatDateParts(
    bucketStart,
    hourly(period) ? WEEKDAY_HOUR : WEEKDAY_YEAR,
    language,
  )
}

const countFormats = new Map<string, Intl.NumberFormat>()

/** A count with the language's grouping (`-1,234`). */
export function formatCount(value: number, language?: AdminLanguage) {
  const locale = intlLocale(language)
  let format = countFormats.get(locale)
  if (format === undefined) {
    format = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 })
    countFormats.set(locale, format)
  }
  return format.format(value)
}

/** How many of its unit each period covers. */
export const PERIOD_COUNTS: Record<
  DashboardPeriod,
  { unit: 'hours' | 'days'; count: number }
> = {
  '24h': { unit: 'hours', count: 24 },
  '7d': { unit: 'days', count: 7 },
  '30d': { unit: 'days', count: 30 },
}

/** A period's name (`24 hours`, `7 days`) for the period select. */
export function periodLabel(t: TFunction, period: DashboardPeriod) {
  const { unit, count } = PERIOD_COUNTS[period]
  return unit === 'hours'
    ? t('dashboardPage.period.hours', { count })
    : t('dashboardPage.period.days', { count })
}

/** A short calendar day (`Jan 14, 2026`) of `YYYY-MM-DD`. */
export function formatDay(day: string, language?: AdminLanguage) {
  // the day is a calendar date: formatted on UTC, it is the same everywhere
  return isBerlinDate(day)
    ? formatDateParts(
        new Date(`${day}T12:00Z`),
        { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' },
        language,
      )
    : day
}

/** The window the cards cover: `Last 7 days` or `7 days to Jan 14, 2026`. */
export function periodDescription(
  t: TFunction,
  period: DashboardPeriod,
  until: string | undefined,
  language?: AdminLanguage,
) {
  const { unit, count } = PERIOD_COUNTS[period]
  if (until === undefined) {
    return unit === 'hours'
      ? t('dashboardPage.window.lastHours', { count })
      : t('dashboardPage.window.lastDays', { count })
  }
  const date = formatDay(until, language)
  return unit === 'hours'
    ? t('dashboardPage.window.hoursUntil', { count, date })
    : t('dashboardPage.window.daysUntil', { count, date })
}
